import { jsx } from "react/jsx-runtime";
import { useState, useEffect, createContext } from "react";
import { setServerUrl, createMax, fetchRate } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
import { RENT_VS_BUY_DEFAULT_VALUES } from "./RentVsBuyCalculator.constants.esm.js";
const HOUSING_TRENDS_API = "/api/rsps/v1/get-housing-trends-data";
const fetchRentVsBuyData = async (purchasePrice, downPaymentPercent, regionIdOverride) => {
  var _a, _b, _c, _d, _e;
  const fetched = {};
  try {
    setServerUrl();
    createMax();
    const mortgageRate = await fetchRate({
      homePrice: purchasePrice,
      downPayment: purchasePrice * downPaymentPercent / 100
    });
    if (mortgageRate) {
      fetched.mortgageRate = mortgageRate;
    }
  } catch (error) {
    console.error("RVB: failed to fetch mortgage rate", error);
  }
  let regionId = regionIdOverride;
  if (!regionId) {
    try {
      const mortgageBase = (_b = (_a = window._zconfig) == null ? void 0 : _a.serverURLs) == null ? void 0 : _b.MAX;
      const partnerId = (_c = window._zconfig) == null ? void 0 : _c.partnerId;
      if (mortgageBase && partnerId) {
        const locationURL = new URL("/whereAmI", mortgageBase);
        locationURL.searchParams.set("partnerId", partnerId);
        const locationResponse = await fetch(locationURL.href, {
          credentials: "same-origin",
          signal: AbortSignal.timeout(3e3)
        });
        const locationData = await locationResponse.json();
        if (locationResponse.ok) {
          regionId = locationData.cityRegionId;
        }
      }
    } catch (error) {
      console.error("RVB: failed to fetch location", error);
    }
  }
  if (regionId) {
    try {
      const odpResponse = await fetch(`${HOUSING_TRENDS_API}?regionid=${regionId}`, {
        signal: AbortSignal.timeout(3e3)
      });
      if (odpResponse.ok) {
        const ODPdata = await odpResponse.json();
        const currentHomePrice = (_d = ODPdata.dataPayload) == null ? void 0 : _d.averageHomePrice;
        const currentRent = (_e = ODPdata.dataPayload) == null ? void 0 : _e.averageRent;
        if (currentHomePrice > 0) {
          fetched.purchasePrice = currentHomePrice;
        }
        if (currentRent > 0) {
          fetched.monthlyRent = currentRent;
        }
      }
    } catch (error) {
      console.error("RVB: failed to fetch housing trends", error);
    }
  }
  return fetched;
};
const DataContext = createContext({
  currentState: RENT_VS_BUY_DEFAULT_VALUES,
  setCurrentState: () => void 0
});
const DataProvider = ({
  children,
  initialPurchasePrice,
  initialDownPaymentPercent,
  initialMonthlyRent,
  initialMortgageRate,
  regionId
}) => {
  const [currentState, setCurrentState] = useState({
    ...RENT_VS_BUY_DEFAULT_VALUES,
    ...initialPurchasePrice !== void 0 && {
      purchasePrice: initialPurchasePrice
    },
    ...initialDownPaymentPercent !== void 0 && {
      downPaymentPercent: initialDownPaymentPercent
    },
    ...initialMonthlyRent !== void 0 && {
      monthlyRent: initialMonthlyRent
    },
    ...initialMortgageRate !== void 0 && {
      mortgageRate: initialMortgageRate
    }
  });
  useEffect(() => {
    const purchasePrice = initialPurchasePrice ?? RENT_VS_BUY_DEFAULT_VALUES.purchasePrice;
    const downPaymentPercent = initialDownPaymentPercent ?? RENT_VS_BUY_DEFAULT_VALUES.downPaymentPercent;
    fetchRentVsBuyData(purchasePrice, downPaymentPercent, regionId).then((fetched) => {
      setCurrentState((prev) => ({
        ...prev,
        // Only update from fetch for fields not explicitly set by block attrs
        ...initialMortgageRate === void 0 && fetched.mortgageRate !== void 0 && {
          mortgageRate: fetched.mortgageRate
        },
        ...initialPurchasePrice === void 0 && fetched.purchasePrice !== void 0 && {
          purchasePrice: fetched.purchasePrice
        },
        ...initialMonthlyRent === void 0 && fetched.monthlyRent !== void 0 && {
          monthlyRent: fetched.monthlyRent
        }
      }));
    });
  }, []);
  return /* @__PURE__ */ jsx(DataContext.Provider, { value: {
    currentState,
    setCurrentState
  }, children });
};
export {
  DataContext,
  DataProvider
};
